import { ApplicationElement, html } from '../application_element/element.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Wrapper for organize options that also counts helpers.
 *
 * @prop totalCount - Total number of helpers. It will be updated by component when user adds or removes help.
 * @prop resultsLink - When provided wraps 'n members assigned' label to a link (optional)
 * @prop isClosed - When true, disables voting and renders closed voting message (optional)
 *
 * @cssprop [--options-padding: 16px 0 16px 0] Padding of each slotted option
 * @cssprop [--options-border-bottom: 1px solid var(--border-light)] Bottom border of each slotted option
 * @cssprop [--notes-padding: 16px 0 16px 0] Padding of notes section
 * @cssprop [--notes-color: var(--dark-text-secondary)] Color of notes section text
 * @cssprop [--notes-font-size: var(--font-size-sm, 12px)] Size of notes section text
 * @cssprop [--notes-font-style: italic] Style of notes section text
 * @cssprop [--notes-gap: 4px] Gap between notes in notes section
 *
 *
 * @slot default - For organize options
 * @slot avatars - For avatar list
 * @slot actions - For additional actions in footer
 */

export default class OrganizeOptionWrapper extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    resultsLink: { type: String },
    isClosed: { type: Boolean },
    totalCount: { type: Number },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.totalCount = 0;
  }

  /** @internal */
  get options() {
    return Array.from(this.querySelectorAll('ef-organize-option'));
  }

  /** @internal */
  get checkedCount() {
    return this.options.filter(o => o.checked).length;
  }

  handleSlotChange() {
    this.options.forEach(o => {
      o.disabled = this.isClosed;
      o.AppConfig = this.AppConfig;
    });
  }

  handleCheckedChange(e) {
    const checked = e.target.checked;
    if (checked && this.checkedCount === 1) this.totalCount++;
    else if (!checked && this.checkedCount === 0) this.totalCount--;
  }

  renderTotalAssigned() {
    const label =
      this.isClosed && !this.totalCount
        ? this.t('no_assignees')
        : this.t('assignees', { count: this.totalCount });

    return this.resultsLink
      ? html`<a href=${this.resultsLink}>${label}</a>`
      : label;
  }

  renderClosedLabel() {
    return html`<div>${this.t('closed')}</div>`;
  }

  render() {
    return html`
      <slot
        @organizeOption-change=${this.handleCheckedChange}
        @slotchange=${this.handleSlotChange}>
      </slot>
      <div class="note">
        <div>${this.renderTotalAssigned()} <slot name="actions"></slot></div>
        ${this.isClosed ? this.renderClosedLabel() : null}
      </div>
    `;
  }
}

OrganizeOptionWrapper.registerTagName('ef-organize-option-wrapper');

customElements.get('ef-organize-option-wrapper') ||
  customElements.define('ef-organize-option-wrapper', OrganizeOptionWrapper);
